import React, {useState} from 'react'
import {Grid} from '@mui/material'
import moment from 'moment'
import SecurSpaceContainer from '../../components/common/SecurSpaceContainer'
import {
  SupplierAccountsReceivableContent,
  SupplierAccountsReceivableFilter,
  SupplierAccountsReceivableSummary
} from '../../components/invoices/supplier-accounts-receivable'
import {getServiceFeeDisplayValue} from '../../components/invoices/serviceFeeHelper.js';
import {getSupplierAccountsReceivable} from '../../requests/reports/invoices/supplier-accounts-receivable-requests'
import {supplierAccountsReceivableReportEndpoints} from '../../requests/api-endpoints'
import {BookingTransactionTypeLabel, DateFormats} from '../../components/constants/securspace-constants'
import {getErrorMessageForStandardResponse} from '../../util/NetworkErrorUtil'
import {formatCurrencyValue} from '../../util/PaymentUtils'
import getDisplayValue from '../../components/SupplierTransactionStatus'
import useUrlQueryParams from "../../hooks/useUrlQueryParams";
import {useCollapseById} from "../../hooks/report/useCollapseById";
import {SupplierAccountsReceivableRequestType} from "../../types/reports/SupplierAccountsReceivableRequestType";
import Pagination from "../../components/deprecatedUikit/Pagination/Pagination";
import InfoCardLoader from "../../components/deprecatedUikit/InfoCard/InfoCardLoader";
import {SecurSpaceReportContainer, SecurSpaceReportTitle} from "../../components/common";
import {useGetBoundReportSummary} from "../../hooks/request/useGetBoundReportSummary";
import {
  SupplierAccountsReceivableSummary as SupplierAccountsReceivableSummaryType,
  supplierAccountsReceivableSummaryMapping
} from "../../types/model/valueObject/invoice/SupplierAccountsReceivableSummary";
import {ReportSummaryWithLoader} from "../../components/report/redesigned";
import {useSnackbar} from "../../context/snackbar-context";

interface InvoiceItem {
  transactionId: string;
  supplierAmount: string;
  transactionType: string;
  status: string;
  serviceFeeType: string;
  id: string;
  [key: string]: unknown;
}

const SupplierAccountsReceivableReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  const { invoiceNumber: urlQueryInvoiceNumber, startDate: urlQueryStartDate } = useUrlQueryParams();
  const defaultStartDate = urlQueryStartDate && moment(urlQueryStartDate).isValid() ?
    moment(urlQueryStartDate).format(DateFormats.DAY) :
    moment().add(-1, 'months').format(DateFormats.DAY);
  const defaultEndDate = moment(defaultStartDate).add(1, 'months').format(DateFormats.DAY);

  const defaultQuery: SupplierAccountsReceivableRequestType = {
    page: 0,
    size: 50,
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    locationId: "",
    bookingNumber: "",
    transactionNumber: urlQueryInvoiceNumber || "",
    buyerId: "",
    buyerAccountId: "",
    isUnpaidOnly: false,
    sortBy: "TRANSACTION_NUMBER",
    sortDir: "desc"
  };

  const [reportQuery, setReportQuery] = useState<SupplierAccountsReceivableRequestType>({...defaultQuery});
  const [loading, setLoading] = useState<boolean>(true);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [totalNumberResults, setTotalNumberResults] = useState<number>(0);

  const {
    isExpanded,
    handleToggle,
    handleExpandAll,
    setExpandedAll,
    setExpandedItems,
    hasExpandedItems,
  } = useCollapseById({ items: invoices });

  const {
    reportSummary,
    isReportSummaryLoading
  } = useGetBoundReportSummary<SupplierAccountsReceivableSummaryType>({
    requestUrl: supplierAccountsReceivableReportEndpoints.summary,
    requestParams: reportQuery
  });

  const getData = (filteredQuery: SupplierAccountsReceivableRequestType): void => {
    setLoading(true);
    setReportQuery(filteredQuery);
    getSupplierAccountsReceivable(filteredQuery)!
      .then((response) => {
        const {content, count} = response.body;
        const data: InvoiceItem[] = content?.map((item: Record<string, unknown>) => {
          return {
            ...item,
            supplierAmount: formatCurrencyValue(item.supplierAmount as number),
            transactionType: BookingTransactionTypeLabel[item?.transactionType as keyof typeof BookingTransactionTypeLabel] || item?.transactionType,
            status: getDisplayValue(item?.status as string),
            id: item?.transactionId,
            serviceFeeType: getServiceFeeDisplayValue(item?.serviceFeeType),
          } as InvoiceItem;
        }) ?? [];
        setTotalNumberResults(count);
        setInvoices(data);
        // expanded state auto-resets via useCollapseById when `invoices` changes
      }).catch((err: unknown) => {
        snackbarShowMessage(getErrorMessageForStandardResponse(err, "Failed to load accounts receivable"), "error", 15000)
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateFilteredQuery = (filteredQuery: SupplierAccountsReceivableRequestType): void => {
    const newFilteredQuery: SupplierAccountsReceivableRequestType = {
       ...filteredQuery,
       size: reportQuery.size,
       page: 0
     }
    getData(newFilteredQuery);
  }

  const updatePaging = (page: number, size: number): void => {
    if (page !== reportQuery.page || size !== reportQuery.size) {
      const newFilteredQuery: SupplierAccountsReceivableRequestType = {
        ...reportQuery,
        page,
        size
      }
      getData(newFilteredQuery);
    }
  }

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle mb={4}>Accounts Receivable</SecurSpaceReportTitle>
        <SupplierAccountsReceivableFilter
          initialQuery={defaultQuery}
          filterCallback={updateFilteredQuery}
        />
        <ReportSummaryWithLoader
          visible={invoices.length > 0}
          isLoading={isReportSummaryLoading}
          mapping={supplierAccountsReceivableSummaryMapping}
          summary={reportSummary}
        />
        {
          invoices.length > 0 &&
           <SupplierAccountsReceivableSummary
             title={'All'}
             count={invoices.length}
             setExpandedItems={setExpandedItems}
             setExpandedAll={setExpandedAll}
             isShowCollapseButton={true}
             handleExpandAll={handleExpandAll}
             hasExpandedItems={hasExpandedItems}
             query={reportQuery}
           />
        }
        {
          loading ? [...Array(10).keys()].map((_, index) => (
            <InfoCardLoader key={index} />
          )) : <SupplierAccountsReceivableContent
            loading={loading}
            invoices={invoices}
            handleToggle={handleToggle}
            isExpanded={isExpanded}
          />
        }
        {
          invoices.length > 0 && <Grid item>
            <Pagination
              count={totalNumberResults}
              rowsPerPage={reportQuery.size ?? 50}
              page={reportQuery.page ?? 0}
              onPageChange={(_event: unknown, page: number) => updatePaging(page, reportQuery.size ?? 50)}
              onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updatePaging(0, Number(event.target.value))}
              variant="tablePagination"
              scrollToBottomOnPageChange={true}
            />
          </Grid>
        }
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  )
}

export default SupplierAccountsReceivableReport;
